import { z } from "zod";
import { CoaTemplateApplySource } from "@common/types/domains/accounting/chartOfAccountsTemplateApplySource";
import { accountRoleSchema } from "./account.schema";
import { coaPostApplySnapshotSchema } from "./chart-of-accounts-template-post-apply-snapshot.schema";

const isoDateTimeSchema = z.string().datetime({ offset: true });

export const coaApplyUndoSkipReasonSchema = z.enum([
    "changed_since_applied",
    "in_use",
    "missing",
    "post_apply_activity",
]);

export const coaApplyUndoItemStatusSchema = z.enum(["reverted", "skipped"]);

export const coaApplyUndoItemResultSchema = z
    .object({
        status: coaApplyUndoItemStatusSchema,
        reason: coaApplyUndoSkipReasonSchema.optional(),
        at: isoDateTimeSchema,
    })
    .strict();

export const coaApplyGroupRefSchema = z
    .object({
        groupId: z.string().uuid().nullable(),
        groupPath: z.array(z.string()),
    })
    .strict();

const nullableNumberFieldChangeSchema = z
    .object({
        before: z.number().nullable(),
        after: z.number().nullable(),
    })
    .strict();

const nullableUuidFieldChangeSchema = z
    .object({
        before: z.string().uuid().nullable(),
        after: z.string().uuid().nullable(),
    })
    .strict();

const stringFieldChangeSchema = z
    .object({
        before: z.string(),
        after: z.string(),
    })
    .strict();

const nullableStringFieldChangeSchema = z
    .object({
        before: z.string().nullable(),
        after: z.string().nullable(),
    })
    .strict();

export const coaApplyCreatedAccountSchema = z
    .object({
        accountId: z.string().uuid(),
        code: z.string(),
        codeText: z.string().nullable(),
        name: z.string(),
        type: z.string(),
        isDisabled: z.boolean(),
        isManuallyCreated: z.boolean(),
        role: accountRoleSchema.unwrap(),
        group: coaApplyGroupRefSchema,
        sortOrder: z.number().nullable(),
        sourceTemplateAccountId: z.string().uuid(),
        undoResult: coaApplyUndoItemResultSchema.optional(),
    })
    .strict();

export const coaApplyUpdatedAccountSchema = z
    .object({
        accountId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        type: z.string(),
        isManuallyCreated: z.boolean(),
        changes: z
            .object({
                name: stringFieldChangeSchema.optional(),
                code: stringFieldChangeSchema.optional(),
                codeText: nullableStringFieldChangeSchema.optional(),
                group: z
                    .object({
                        before: coaApplyGroupRefSchema,
                        after: coaApplyGroupRefSchema,
                    })
                    .strict()
                    .optional(),
                isDisabled: z
                    .object({
                        before: z.boolean(),
                        after: z.boolean(),
                    })
                    .strict()
                    .optional(),
                sortOrder: nullableNumberFieldChangeSchema.optional(),
                sourceTemplateAccountId:
                    nullableUuidFieldChangeSchema.optional(),
            })
            .strict(),
        undoResult: coaApplyUndoItemResultSchema.optional(),
    })
    .strict();

export const coaApplyCreatedGroupParentSchema = z
    .object({
        groupId: z.string().uuid(),
        name: z.string(),
    })
    .strict();

export const coaApplyCreatedGroupSchema = z
    .object({
        groupId: z.string().uuid(),
        name: z.string(),
        parentGroupId: z.string().uuid().nullable(),
        type: z.string(),
        parentChain: z.array(coaApplyCreatedGroupParentSchema),
        undoResult: coaApplyUndoItemResultSchema.optional(),
    })
    .strict();

export const coaApplyVariableMappingChangeSchema = z
    .object({
        variableKind: z.string(),
        before: z.string().uuid().nullable(),
        after: z.string().uuid().nullable(),
        undoResult: coaApplyUndoItemResultSchema.optional(),
    })
    .strict();

export const coaTemplateApplyChangeSetSchema = z
    .object({
        createdAccounts: z.array(coaApplyCreatedAccountSchema),
        updatedAccounts: z.array(coaApplyUpdatedAccountSchema),
        createdGroups: z.array(coaApplyCreatedGroupSchema),
        variableMappingChanges: z.array(coaApplyVariableMappingChangeSchema),
    })
    .strict();

export const chartOfAccountsTemplateApplyEventPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    eventId: z.string().uuid(),
});

export const coaTemplateApplyUndoBodySchema = z.object({
    undoInvocationId: z.string().uuid(),
});

export const chartOfAccountsTemplateApplyEventDetailSchema = z.object({
    id: z.string().uuid(),
    applyInvocationId: z.string().uuid().nullable(),
    templateId: z.string().uuid().nullable(),
    templateName: z.string(),
    entityId: z.number().int(),
    workspaceId: z.string().uuid(),
    appliedByUserId: z.number().int(),
    source: z.nativeEnum(CoaTemplateApplySource),
    changeSet: coaTemplateApplyChangeSetSchema,
    postApplySnapshot: coaPostApplySnapshotSchema,
    revertedAt: isoDateTimeSchema.nullable(),
    revertedByUserId: z.number().int().nullable(),
    undoInvocationId: z.string().uuid().nullable(),
    revertedByUserName: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});

export const coaApplyUndoItemKindSchema = z.enum([
    "createdAccount",
    "updatedAccount",
    "createdGroup",
    "variableMapping",
]);

export const coaApplyUndoEligibilityItemSchema = z.object({
    kind: coaApplyUndoItemKindSchema,
    id: z.string(),
    eligible: z.boolean(),
    reason: coaApplyUndoSkipReasonSchema.optional(),
});

export const coaApplyUndoEligibilitySchema = z.object({
    superseded: z.boolean(),
    eligibleCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    items: z.array(coaApplyUndoEligibilityItemSchema),
});

export const coaApplyUndoResultItemSchema = z.object({
    kind: coaApplyUndoItemKindSchema,
    id: z.string(),
    status: coaApplyUndoItemStatusSchema,
    reason: coaApplyUndoSkipReasonSchema.optional(),
    at: isoDateTimeSchema,
});

export const coaApplyUndoResponseSchema = z.object({
    eventId: z.string().uuid(),
    undoInvocationId: z.string().uuid(),
    eventRevertedAt: isoDateTimeSchema.nullable(),
    revertedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    items: z.array(coaApplyUndoResultItemSchema),
});

export type CoaApplyUndoSkipReason = z.infer<
    typeof coaApplyUndoSkipReasonSchema
>;
export type CoaApplyUndoItemResult = z.infer<
    typeof coaApplyUndoItemResultSchema
>;
export type CoaApplyGroupRef = z.infer<typeof coaApplyGroupRefSchema>;
export type CoaApplyCreatedAccount = z.infer<
    typeof coaApplyCreatedAccountSchema
>;
export type CoaApplyUpdatedAccount = z.infer<
    typeof coaApplyUpdatedAccountSchema
>;
export type CoaApplyCreatedGroupParent = z.infer<
    typeof coaApplyCreatedGroupParentSchema
>;
export type CoaApplyCreatedGroup = z.infer<typeof coaApplyCreatedGroupSchema>;
export type CoaApplyVariableMappingChange = z.infer<
    typeof coaApplyVariableMappingChangeSchema
>;
export type CoaTemplateApplyChangeSet = z.infer<
    typeof coaTemplateApplyChangeSetSchema
>;
export type CoaApplyUndoItemStatus = z.infer<
    typeof coaApplyUndoItemStatusSchema
>;
export type CoaApplyUndoItemKind = z.infer<typeof coaApplyUndoItemKindSchema>;
export type CoaApplyUndoEligibilityItem = z.infer<
    typeof coaApplyUndoEligibilityItemSchema
>;
export type ChartOfAccountsTemplateApplyEventPathParams = z.infer<
    typeof chartOfAccountsTemplateApplyEventPathParamsSchema
>;
export type CoaTemplateApplyUndoBody = z.infer<
    typeof coaTemplateApplyUndoBodySchema
>;
export type ChartOfAccountsTemplateApplyEventDetail = z.infer<
    typeof chartOfAccountsTemplateApplyEventDetailSchema
>;
export type CoaApplyUndoEligibility = z.infer<
    typeof coaApplyUndoEligibilitySchema
>;
export type CoaApplyUndoResponse = z.infer<typeof coaApplyUndoResponseSchema>;
